import { supabase } from '@/lib/supabaseClient';

export interface ChecklistGroup {
  id: string;
  template_id: number;
  tenant_id: string;
  group_name: string;
  group_order: number;
  permissions?: string[] | null;
  user_permissions?: string[] | null;
  conditional_logic?: any | null;
  created_at: string;
  updated_at: string;
  items?: ChecklistItem[];
  // Phase 1: Repeatable Groups fields
  parent_group_id?: string | null;
  is_repeatable?: boolean;
  min_instances?: number | null;
  max_instances?: number | null;
  trigger_type?: 'numeric_input' | 'user_button' | 'conditional' | null;
  trigger_field_id?: number | null;
  // Nested groups support
  children?: ChecklistGroup[];
}

export interface ChecklistItem {
  id: number | string; // Can be number (pipeline mode) or string (template mode)
  template_id: number;
  group_id?: string | null;
  item_order: number;
  item_name: string;
  description?: string | null; // Optional description text displayed below item name
  item_type: string;
  is_required: boolean;
  item_values?: any;
  permissions?: string[] | null;
  user_permissions?: string[] | null;
  conditional_logic?: any | null;
  allow_attachments?: boolean; // Enable photo attachments for this field
  created_at: string;
  updated_at: string;
}

export interface ChecklistGroupsResponse {
  groups: ChecklistGroup[];
  ungroupedItems: ChecklistItem[];
  template_id: number;
}

class ChecklistGroupsService {
  private supabaseUrl: string;

  constructor() {
    this.supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
  }

  /**
   * Fetch checklist groups with items for a template
   */
  async getChecklistGroups(templateId: number): Promise<ChecklistGroupsResponse> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(
        `${this.supabaseUrl}/functions/v1/get-checklist-groups?template_id=${templateId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching checklist groups:', error);
      throw error;
    }
  }

  /**
   * Batch fetch checklist groups for multiple templates
   * This is much more efficient than calling getChecklistGroups for each template individually
   */
  async getChecklistGroupsBatch(templateIds: number[]): Promise<{[templateId: string]: ChecklistGroupsResponse}> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      // Use direct database queries for better performance instead of Edge Functions
      const results: {[templateId: string]: ChecklistGroupsResponse} = {};

      // Fetch all groups for the given template IDs
      const { data: groups, error: groupsError } = await supabase
        .from('checklist_groups')
        .select('*')
        .in('template_id', templateIds)
        .order('group_order');

      if (groupsError) {
        throw groupsError;
      }

      // Fetch all items for these templates (excluding soft-deleted items)
      const { data: items, error: itemsError } = await supabase
        .from('checklist_items')
        .select('*')
        .in('template_id', templateIds)
        .is('deleted_at', null)
        .order('item_order');

      if (itemsError) {
        throw itemsError;
      }

      // Group items by template_id
      const itemsByTemplate: {[templateId: string]: ChecklistItem[]} = {};
      if (items) {
        items.forEach(item => {
          const templateId = item.template_id.toString();
          if (!itemsByTemplate[templateId]) {
            itemsByTemplate[templateId] = [];
          }
          itemsByTemplate[templateId].push(item);
        });
      }

      // Group groups by template_id and attach items
      const groupsByTemplate: {[templateId: string]: ChecklistGroup[]} = {};
      if (groups) {
        groups.forEach(group => {
          const templateId = group.template_id.toString();
          if (!groupsByTemplate[templateId]) {
            groupsByTemplate[templateId] = [];
          }
          
          // Attach items to this group
          const groupItems = itemsByTemplate[templateId]?.filter(item => item.group_id === group.id) || [];
          groupsByTemplate[templateId].push({
            ...group,
            items: groupItems
          });
        });
      }

      // Build hierarchical structure helper
      const buildHierarchy = (groups: ChecklistGroup[]): ChecklistGroup[] => {
        const topLevelGroups = groups.filter(g => !g.parent_group_id);
        const childGroups = groups.filter(g => g.parent_group_id);

        const attachChildren = (group: ChecklistGroup): ChecklistGroup => {
          const children = childGroups.filter(child => child.parent_group_id === group.id);
          return {
            ...group,
            children: children.length > 0 ? children.map(attachChildren) : undefined
          };
        };

        return topLevelGroups.map(attachChildren);
      };

      // Create response for each template with hierarchical structure
      templateIds.forEach(templateId => {
        const templateGroups = groupsByTemplate[templateId.toString()] || [];
        const templateItems = itemsByTemplate[templateId.toString()] || [];
        const ungroupedItems = templateItems.filter(item => !item.group_id);

        results[templateId.toString()] = {
          groups: buildHierarchy(templateGroups),
          ungroupedItems: ungroupedItems,
          template_id: templateId
        };
      });

      return results;
    } catch (error) {
      console.error('Error batch fetching checklist groups:', error);
      throw error;
    }
  }

  /**
   * Create a new checklist group
   */
  async createGroup(groupData: Omit<ChecklistGroup, 'id' | 'created_at' | 'updated_at'>): Promise<ChecklistGroup> {
    try {
      const { data, error } = await supabase
        .from('checklist_groups')
        .insert([groupData])
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data as ChecklistGroup;
    } catch (error) {
      console.error('Error creating checklist group:', error);
      throw error;
    }
  }

  /**
   * Update an existing checklist group
   */
  async updateGroup(groupData: ChecklistGroup): Promise<ChecklistGroup> {
    try {
      const { id, created_at, updated_at, ...rest } = groupData;

      // Only include fields that exist in the database schema
      const updateData: Record<string, any> = {};
      const allowedFields = ['template_id', 'tenant_id', 'group_name', 'group_order', 'permissions', 'user_permissions', 'conditional_logic'];

      for (const field of allowedFields) {
        if (field in rest) {
          updateData[field] = (rest as any)[field];
        }
      }

      const { data, error } = await supabase
        .from('checklist_groups')
        .update(updateData)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (!data) {
        throw new Error(`Checklist group with ID ${id} not found in database. It may have been deleted or was never saved.`);
      }

      return data as ChecklistGroup;
    } catch (error) {
      console.error('Error updating checklist group:', error);
      throw error;
    }
  }

  /**
   * Delete a checklist group
   */
  async deleteGroup(groupId: string): Promise<{ success: boolean; message: string }> {
    try {
      const { error } = await supabase
        .from('checklist_groups')
        .delete()
        .eq('id', groupId);

      if (error) {
        throw error;
      }

      return { success: true, message: 'Group deleted successfully' };
    } catch (error) {
      console.error('Error deleting checklist group:', error);
      throw error;
    }
  }

  /**
   * Reorder checklist groups
   */
  async reorderGroups(templateId: number, groups: ChecklistGroup[]): Promise<{ success: boolean; message: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(
        `${this.supabaseUrl}/functions/v1/reorder-checklist`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'groups',
            templateId,
            items: groups,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error reordering groups:', error);
      throw error;
    }
  }

  /**
   * Reorder checklist items within groups
   */
  async reorderItems(templateId: number, items: ChecklistItem[]): Promise<{ success: boolean; message: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(
        `${this.supabaseUrl}/functions/v1/reorder-checklist`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'items',
            templateId,
            items: items,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error reordering items:', error);
      throw error;
    }
  }

  /**
   * Assign items to groups or ungroup them
   */
  async assignItemsToGroup(itemIds: number[], groupId: string | null): Promise<void> {
    try {
      const updates = itemIds.map(id => ({
        id,
        group_id: groupId,
      }));

      const { error } = await supabase
        .from('checklist_items')
        .upsert(updates, { onConflict: 'id' });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error assigning items to group:', error);
      throw error;
    }
  }

  /**
   * Update a checklist item's allow_attachments setting
   */
  async updateItemAttachmentsSetting(itemId: number, allowAttachments: boolean): Promise<void> {
    try {
      const { error } = await supabase
        .from('checklist_items')
        .update({ allow_attachments: allowAttachments })
        .eq('id', itemId);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error updating item attachments setting:', error);
      throw error;
    }
  }
}

export const checklistGroupsService = new ChecklistGroupsService(); 